import { RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { useTableSort } from "@/components/sortable-table-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type ProjectSummary, useProjects, useRefreshProjects } from "@/lib/api";
import { logger } from "@/lib/logger";

import { ProjectDeleteDialog } from "./project-delete-dialog";
import { ProjectTable } from "./project-table";

// Sentinel owner-filter value for projects that have no owner. Cannot collide
// with a real owner email (emails always contain "@").
const OWNER_UNASSIGNED = "__unassigned__";

export function AdminProjectsPage() {
  // Stalest first by default — this view exists to spot abandoned projects, so
  // order by real activity (last commit, NOR-325; falls back to the DB
  // timestamp server-side). Ordering is server-side; header clicks change it.
  const { sortBy, sortDir, onSort } = useTableSort({ sortBy: "last_commit_at", sortDir: "asc" });
  const { data: projects = [], isLoading: loading } = useProjects(true, {
    sortBy: sortBy ?? undefined,
    sortDir,
  });
  const refresh = useRefreshProjects();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [deleteProject, setDeleteProject] = useState<ProjectSummary | null>(null);

  const statuses = useMemo(() => [...new Set(projects.map((p) => p.status))].sort(), [projects]);

  // Distinct owners keyed by email (the stable identifier); the label prefers
  // the display name. Projects without an owner collapse into one "Unknown"
  // bucket under the OWNER_UNASSIGNED sentinel -- matching the table's own
  // no-owner label so the option reads the same as the column.
  const owners = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const p of projects) {
      const key = p.owner_email ?? OWNER_UNASSIGNED;
      if (!byKey.has(key)) {
        byKey.set(key, p.owner_name || p.owner_email || "Unknown");
      }
    }
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Ordering is server-side (sortBy/sortDir); only filter here.
    return projects
      .filter((p) => statusFilter === "all" || p.status === statusFilter)
      .filter((p) => ownerFilter === "all" || (p.owner_email ?? OWNER_UNASSIGNED) === ownerFilter)
      .filter(
        (p) =>
          !q ||
          p.project_name.toLowerCase().includes(q) ||
          p.project_slug.toLowerCase().includes(q) ||
          (p.owner_email ?? "").toLowerCase().includes(q) ||
          (p.owner_name ?? "").toLowerCase().includes(q),
      );
  }, [projects, search, statusFilter, ownerFilter]);

  const outOfSync = refresh.data?.out_of_sync ?? [];

  const handleRefresh = async () => {
    try {
      await refresh.mutateAsync();
    } catch (err) {
      logger.error("[admin-projects] repo check failed:", err);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-primary text-2xl font-normal">Projects</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            All scaffolded projects &mdash; {projects.length} project
            {projects.length !== 1 ? "s" : ""}, stalest first
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refresh.isPending}>
          <RefreshCw className={refresh.isPending ? "mr-2 size-4 animate-spin" : "mr-2 size-4"} />
          {refresh.isPending ? "Checking repos..." : "Check repos"}
        </Button>
      </div>

      {/* Out-of-sync results from the repo check */}
      {refresh.isError && (
        <p className="text-destructive mt-4 text-sm">
          Repo check failed. Please try again in a moment.
        </p>
      )}
      {refresh.isSuccess && (
        <div className="border-border bg-card mt-4 rounded-lg border p-4 text-sm">
          {outOfSync.length === 0 ? (
            <p className="text-muted-foreground">
              Repo check complete — every project&apos;s GitHub repository was found.
            </p>
          ) : (
            <>
              <p className="text-destructive font-medium">
                {outOfSync.length} project{outOfSync.length !== 1 ? "s" : ""} out of sync (GitHub
                repo not found):
              </p>
              <ul className="text-muted-foreground mt-2 list-inside list-disc">
                {outOfSync.map((p) => (
                  <li key={p.id}>
                    {p.project_name} <span className="text-xs">({p.project_slug})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Table card */}
      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {/* Search + status filter inside card */}
        <div className="border-border flex items-center gap-3 border-b px-4 py-3">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by project, slug, or owner..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map((s) => {
                const label = s.replace(/_/g, " ");
                return (
                  <SelectItem key={s} value={s}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">Loading projects...</div>
        ) : (
          <ProjectTable
            projects={filtered}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onDelete={setDeleteProject}
          />
        )}
      </div>

      <ProjectDeleteDialog
        project={deleteProject}
        open={deleteProject !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteProject(null);
          }
        }}
      />
    </div>
  );
}
